import React, { ReactElement, useState, useEffect } from 'react'
import gql from 'graphql-tag'
import useForm from 'react-hook-form'
import * as yup from 'yup'
import { useGetUserQuery, useEditUserMutation, EditUserMutationVariables, useDeleteUserMutation, useGetCurrentUserQuery, useStatesSearchQuery, AllCustomersQueryVariables, useGetSearchCustomersQuery, CustomerSortInput, SortEnumType, useGetSearchCustomersLazyQuery, useGetSearchLocationsLazyQuery } from '../../generated/graphql'
import FormPage from '../../components/General/FormPage/FormPage'
import FormTile from '../../components/Tiles/FormTile/FormTile'
import propName from '../../helpers/propName'
import HookedTextInput from '../../components/General/Inputs/HookedTextInput'
import Button, { ButtonType } from '../../components/General/Button/Button'
import Select, { MultiValue } from 'react-select'
import Modal from '../../components/Modal/Modal'
import HookedCheckbox from '../../components/General/Inputs/HookedCheckbox'
import MultiCarrierSearch from '../../components/Search/CarrierSearch/MultiCarrierSearch'
import { Params, useNavigate, useParams } from 'react-router-dom'
import MultiCustomerSearch from '../../components/Search/CustomerSearch/MultiCustomerSearch'
import MultiCustomerLocationSearch from '../../components/Search/CustomerSearch/MultiCustomerLocationSearch'
import { toast } from 'react-toastify'
import Spacer from '../../components/General/Utility/Spacer'
import { Lock, Unlock } from 'react-feather'
import AsyncSelect from 'react-select/async';


export const GetSingleUserQuery = gql`
    query GetUser($userKey: Int!) {
        userGetSingle(userKey: $userKey) {
            ...innerUser
        }
    }
`

export const EditUserMutation = gql`
    mutation EditUser($user: UserInput!, $email: String, $newPassword: String, $carriers: [Int!], $customers: [Int!], $locations: [Int!], $states: [Int!], $disabledCustomers: [Int!], $disabledLocations: [Int!]) {
        userUpdate(user: $user, newEmail: $email, newPassword: $newPassword, carriers: $carriers, customers: $customers, locations: $locations, states: $states, customerExclusions: $disabledCustomers, locationExclusions: $disabledLocations) {
            ...innerUser
        }
    }
`

interface MatchParams extends Params {
    userKey: string
}

interface Props {
}

interface Option {
    value: any;
    label: any;
}

export default function EditUser(props: Props): ReactElement {
    const navigate = useNavigate()

    const { userKey: key } = useParams() as MatchParams

    const [carrierKeys, setCarrierKeys] = useState<number[]>(undefined)
    const [customerKeys, setCustomerKeys] = useState<number[]>(undefined)
    const [locationKeys, setLocationKeys] = useState<number[]>(undefined)
    const [isAdminUser, setIsAdminUser] = useState<boolean>(false)
    const [hasFullAccess, setHasFullAccess] = useState<boolean>(false)

    const [selectedCustomers, setSelectedCustomers] = useState<MultiValue<Option>>([])
    const [selectedDisabledCustomers, setSelectedDisabledCustomers] = useState<MultiValue<Option>>([])
    const [selectedStates, setSelectedStates] = useState<MultiValue<Option>>([])
    const [selectedLocations, setSelectedLocations] = useState<MultiValue<Option>>([])
    const [selectedDisabledLocations, setSelectedDisabledLocations] = useState<MultiValue<Option>>([])

    const { data: currentUserData, loading: currentUserLoading, error: currentUserError } = useGetCurrentUserQuery()
    const { data: stateData, loading: stateLoading, error: stateError } = useStatesSearchQuery({ variables: { search: "" } })

    const userKey = parseInt(key)
    const [showPasswordError, setShowPasswordError] = useState(false)
    const [currentlyDeletingUser, setCurrentlyDeleting] = useState(false)

    const [isInitialized, setIsInitialized] = useState(false)
    const [customerSearchInput, setCustomerSearchInput] = useState<string>("")

    const validationSchema = yup.object().shape({
        user: yup.object().shape({
            email: yup.string().required().trim(),
            name: yup.string().required().trim(),
            phoneNumber: yup.string().notRequired().trim(),
            passwordHash: yup.string().notRequired(),
            isGasPointUser: yup.boolean().required(),
            isAdminUser: yup.boolean().required(),
            preferPrefixSearch: yup.boolean().required()
        })
    })

    const sort: CustomerSortInput = { name: SortEnumType.Asc }

    // const { data: customerData, loading: customerLoading, error: customerError } = useGetSearchCustomersQuery({ variables: { searchTerm: customerSearchInput, sort } })
    // const showData = !customerLoading && !customerError

    // const results = showData && customerData.customerGetAll.edges.map(customer => {
    //     const display = `${customer.node.dbaName && customer.node.dbaName !== "" ? customer.node.dbaName : customer.node.name}`
    //     return { key: customer.node.customerKey, object: customer, display }
    // })

    const [getCustomers, { loading: customerLoading, data: customerData, error: customerError }] = useGetSearchCustomersLazyQuery();
    const [getLocations, { loading: locationLoading, data: locationData, error: locationError }] = useGetSearchLocationsLazyQuery();


    const { data, loading, error } = useGetUserQuery({ variables: { userKey } })

    const { register, handleSubmit, errors } = useForm<EditUserMutationVariables>({ validationSchema })
    const commonProps = { register, errors }

    const navigateToList = () => {
        navigate('/settings/users')
    }

    useEffect(() => {
        if (data && data.userGetSingle && data.userGetSingle.userCarriers) {
            setCarrierKeys(data.userGetSingle.userCarriers.map(y => y.carrierKey))
        }
    }, [data, setCarrierKeys])

    useEffect(() => {
        if (data) {
            const customerKeysFromPermissions = data.userGetSingle?.userCustomerPermissions || [];
            const customerKeysFromLocations = data.userGetSingle?.userLocationPermissions || [];
            const states = data.userGetSingle?.userStatePermissions || [];
            const customerKeysFromExclusions = data.userGetSingle?.userCustomerExclusions || [];
            const locationKeysFromExclusions = data.userGetSingle?.userLocationExclusions || [];

            setIsAdminUser(data.userGetSingle.isAdminUser)
            setHasFullAccess(data.userGetSingle.hasFullAccess)

            setSelectedCustomers(customerKeysFromPermissions.map(x => ({
                value: x.customerKey,
                label: x.customer.dbaName && x.customer.dbaName !== "" ? x.customer.dbaName : x.customer.name
            })))
            setSelectedLocations(customerKeysFromLocations.map(x => {
                const customerName = x.location.customer.dbaName && x.location.customer.dbaName !== "" ? x.location.customer.dbaName : x.location.customer.name
                return {
                    value: x.locationKey,
                    label: `${customerName} - ${x.location.name ? x.location.name : `${x.location.city}, ${x.location.stateObj?.name}`}`
                }
            }
            ))

            setSelectedStates(states.map(x => ({
                value: x.stateKey,
                label: x.state.name
            })))
            console.log(states)

            setSelectedDisabledCustomers(customerKeysFromExclusions.map(x => ({
                value: x.customerKey,
                label: x.customer.dbaName && x.customer.dbaName !== "" ? x.customer.dbaName : x.customer.name
            })))
            setSelectedDisabledLocations(locationKeysFromExclusions.map(x => {
                const customerName = x.location.customer.dbaName && x.location.customer.dbaName !== "" ? x.location.customer.dbaName : x.location.customer.name
                return {
                    value: x.locationKey,
                    label: `${customerName} - ${x.location.name ? x.location.name : `${x.location.city}, ${x.location.stateObj?.name}`}`
                }
            }))

            // // Combine both arrays and remove duplicates
            // const uniqueCustomerKeys = Array.from(new Set([...customerKeysFromPermissions, ...customerKeysFromLocations]));

            // setCustomerKeys(uniqueCustomerKeys);
            // setLocationKeys(data.userGetSingle?.userLocationPermissions?.map(y => y.locationKey) || []);
            setIsInitialized(true)
        }
    }, [data, setCustomerKeys, setLocationKeys])

    const [EditUser] = useEditUserMutation()
    const [DeleteUser] = useDeleteUserMutation()

    if (currentUserLoading || !currentUserData || loading || stateLoading) {
        return <FormPage></FormPage>
    }

    console.log(selectedLocations)

    const isAdmin = currentUserData.currentUser.isAdminUser
    if (!isAdmin) {
        return <div>You do not have access to user management.</div>
    }

    if (error || currentUserError) {
        return <p>There was a problem loading the user</p>
    }

    const confirmDelete = () => {
        DeleteUser({ variables: { email: data.userGetSingle.email } })
            .then(() => navigateToList())
    }

    const cancelDelete = () => {
        setCurrentlyDeleting(false)
    }

    const openDeleteConfirmation = () => {
        setCurrentlyDeleting(true)
    }

    const addCarrier = (num: number) => {
        if (carrierKeys === undefined) {
            setCarrierKeys([num])
        }
        else if (!carrierKeys.some(x => x === num)) {
            setCarrierKeys([...carrierKeys, num])
        }
    }

    const removeCarrier = (num: number) => {
        setCarrierKeys([...carrierKeys.filter(x => x !== num)])
    }

    const addCustomer = (num: number) => {
        if (customerKeys === undefined) {
            setCustomerKeys([num])
        }
        else if (!customerKeys.some(x => x === num)) {
            setCustomerKeys([...customerKeys, num])
        }
    }

    const removeCustomer = (num: number) => {
        setCustomerKeys([...customerKeys.filter(x => x !== num)])
    }

    const submit = (variables: EditUserMutationVariables) => {

        variables.newPassword = variables.user.passwordHash.trim() !== "" ? variables.user.passwordHash : ""
        if (variables.newPassword !== "") {
            var mediumRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})")
            const matches = mediumRegex.test(variables.newPassword)
            if (!matches) {
                setShowPasswordError(true)
                return null
            }
        }

        variables.email = variables.user.email !== data.userGetSingle.email ? variables.user.email : ""
        variables.user.email = data.userGetSingle.email
        variables.user.userName = data.userGetSingle.email
        variables.user.lockoutEnabled = false
        variables.user.passwordHash = undefined
        variables.carriers = carrierKeys
        // variables.customers = customerKeys
        // variables.locations = locationKeys

        console.log(selectedCustomers)
        console.log(selectedStates)
        variables.customers = selectedCustomers.map(x => x.value)
        variables.locations = selectedLocations.map(x => x.value)
        variables.states = selectedStates.map(x => x.value)
        variables.disabledCustomers = selectedDisabledCustomers.map(x => x.value)
        variables.disabledLocations = selectedDisabledLocations.map(x => x.value)
        console.log(variables)
        EditUser({ variables })
            .then(() => {
                toast.success('User updated successfully')
                navigateToList()
            })
    }


    const options = stateData.stateGetAll.map(x => ({ value: x.stateKey, label: x.name }))

    const onSubmit = handleSubmit(submit)


    const loadCustomerOptions = async (inputValue: string) => {
        const { data: cdata } = await getCustomers({ variables: { searchTerm: inputValue } });

        if (customerError) {
            return [];
        }

        return cdata.customerGetAll.edges.map((customer: any) => ({
            value: customer.node.customerKey,
            label: customer.node.dbaName && customer.node.dbaName !== "" ? customer.node.dbaName : customer.node.name,
        }));
    };

    const loadLocationOptions = async (inputValue: string) => {
        const { data: ldata } = await getLocations({ variables: { searchTerm: inputValue } });

        if (locationError) {
            return [];
        }

        return ldata.locationGetByCustomer
            .map((location: any) => {
                const display = `${location.customer.name} - ${location.name ? location.name : `${location.city}, ${location.stateObj?.name}`}`

                return {
                    value: location.locationKey,
                    label: display,
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    };


    const footerLeftContent = (<div><Button clickAction={navigateToList} buttonType={ButtonType.Secondary}>Cancel</Button>
        <Button clickAction={openDeleteConfirmation} buttonType={ButtonType.Tertiary}>Delete</Button></div>)
    const footerRightContent = (<Button clickAction={onSubmit} buttonType={ButtonType.Success}>Update User</Button>)

    return (
        <FormPage title="Edit User" footerLeftContent={footerLeftContent} footerRightContent={footerRightContent}>
            <form>
                <FormTile>
                    <div className="row">
                        <div className="col-md-6">
                            <HookedTextInput
                                label="Name"
                                propertyKey={propName<EditUserMutationVariables>(o => o.user.name)}
                                defaultValue={data.userGetSingle.name}
                                {...commonProps} />
                        </div>
                        <div className="col-md-6">
                            <HookedTextInput
                                label="Email"
                                propertyKey={propName<EditUserMutationVariables>(o => o.user.email)}
                                defaultValue={data.userGetSingle.email}
                                {...commonProps} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <HookedTextInput
                                label="Phone Number"
                                propertyKey={propName<EditUserMutationVariables>(o => o.user.phoneNumber)}
                                defaultValue={data.userGetSingle.phoneNumber}
                                {...commonProps} />
                        </div>
                        <div className="col-md-6">
                            <HookedTextInput
                                label="Password"
                                propertyKey={propName<EditUserMutationVariables>(o => o.user.passwordHash)}
                                isPassword={true}
                                {...commonProps} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <HookedCheckbox
                                label="GasPoint User"
                                propertyKey={propName<EditUserMutationVariables>(o => o.user.isGasPointUser)}
                                defaultValue={data.userGetSingle.isGasPointUser}
                                {...commonProps}
                            />
                            <HookedCheckbox
                                label="Admin User"
                                propertyKey={propName<EditUserMutationVariables>(o => o.user.isAdminUser)}
                                defaultValue={data.userGetSingle.isAdminUser}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setIsAdminUser(e.target.checked)
                                }}
                                {...commonProps}
                            />
                            <HookedCheckbox
                                label="Has Access to All Customers"
                                propertyKey={propName<EditUserMutationVariables>(o => o.user.hasFullAccess)}
                                defaultValue={data.userGetSingle.hasFullAccess}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setHasFullAccess(e.target.checked)
                                }}
                                isDisabled={isAdminUser}
                                {...commonProps}
                            />
                        </div>
                        <div className="col-md-6">

                            <HookedCheckbox
                                label="Prefer Prefix Search"
                                propertyKey={propName<EditUserMutationVariables>(o => o.user.preferPrefixSearch)}
                                defaultValue={data.userGetSingle.preferPrefixSearch}
                                {...commonProps}
                            />
                        </div>
                        <div className="col-md-4">
                            {showPasswordError && <p>Password must contain at least 8 characters, lowercase, uppercase and numbers</p>}
                        </div>
                    </div>
                </FormTile>
                <FormTile title='Permissions'>
                    {isAdminUser ?
                        <h6>This user is an admin user and has access to all customers.</h6>
                        : hasFullAccess ?
                            <h6>This user has access to all customers.</h6>
                            :
                            data.userGetSingle.isGasPointUser ?
                                <div className="row">
                                    {isInitialized ? <div className="col-md-12">

                                        <h6>State Access</h6>
                                        <Select
                                            options={options}
                                            isMulti
                                            onChange={(selectedOption) => {
                                                setSelectedStates(selectedOption)
                                            }}
                                            defaultValue={selectedStates}
                                        ></Select>
                                        <Spacer spacerSize={20} />
                                        <h6>Customer Access</h6>
                                        <AsyncSelect
                                            loadOptions={loadCustomerOptions}
                                            isMulti
                                            defaultOptions
                                            onChange={(selectedOption) => {
                                                setSelectedCustomers(selectedOption)
                                            }}
                                            defaultValue={selectedCustomers}
                                        ></AsyncSelect>
                                        <Spacer spacerSize={20} />
                                        <h6>Location Access</h6>
                                        <AsyncSelect
                                            loadOptions={loadLocationOptions}
                                            isMulti
                                            onChange={(selectedOption) => {
                                                setSelectedLocations(selectedOption)
                                            }}
                                            defaultValue={selectedLocations}
                                        ></AsyncSelect>
                                        <Spacer spacerSize={20} />
                                        <hr></hr>
                                        <h6>Disable Customer Access</h6>
                                        <span>Regardless of what is selected for the fields in the above section, the user will not have access to the following customers:</span>
                                        <Spacer spacerSize={8} />

                                        <AsyncSelect
                                            loadOptions={loadCustomerOptions}
                                            isMulti
                                            defaultOptions
                                            onChange={(selectedOption) => {
                                                setSelectedDisabledCustomers(selectedOption)
                                            }}
                                            defaultValue={selectedDisabledCustomers}
                                        ></AsyncSelect>
                                        <Spacer spacerSize={20} />
                                        <h6>Disable Location Access</h6>
                                        <span>Regardless of what is selected for the fields in the above section, the user will not have access to the following locations:</span>
                                        <Spacer spacerSize={8} />

                                        <AsyncSelect
                                            loadOptions={loadLocationOptions}
                                            isMulti
                                            onChange={(selectedOption) => {
                                                setSelectedDisabledLocations(selectedOption)
                                            }}
                                            defaultValue={selectedDisabledLocations}
                                        ></AsyncSelect>
                                        <Spacer spacerSize={20} />
                                        {/* {isInitialized ? <MultiCustomerLocationSearch
                                        removeCustomer={removeCustomer}
                                        addCustomer={addCustomer}
                                        updateLocations={setLocationKeys}
                                        data={customerKeys}
                                        locationData={locationKeys}
                                    />

                                        : <p>Loading...</p>} */}
                                    </div>
                                        : <p>Loading...</p>}
                                </div>
                                : <div className="row">
                                    <div className="col-md-12">
                                        <MultiCarrierSearch
                                            removeCarrier={removeCarrier}
                                            addCarrier={addCarrier}
                                            data={carrierKeys}
                                        />
                                    </div>
                                </div>}
                </FormTile>
            </form>
            {currentlyDeletingUser && <Modal headerText="Delete?"
                footerRightContent={<Button clickAction={confirmDelete} buttonType={ButtonType.Primary}>Delete</Button>}
                footerLeftContent={<Button clickAction={cancelDelete} buttonType={ButtonType.Tertiary}>Cancel</Button>}>
                <p>Once you delete this user, it will not be able to be reversed.</p>

            </Modal>}
        </FormPage>
    )
}